import { useCallback, useMemo, useState } from 'react';

import type {
  FilterConfig,
  SelectOption,
} from '@/components/ListFilters/types';

import { useLocalStorage } from './useLocalStorage';

/**
 * Simplified filter definition (declarative)
 * User provides only essential props - hook auto-generates onChange/onClear/value
 */
export type FilterDefinition = {
  type:
    | 'select'
    | 'multiselect'
    | 'boolean'
    | 'date'
    | 'month'
    | 'autocomplete'
    | 'daterange'
    | 'text';
  name: string;
  label: string;
  options?: SelectOption[] | unknown[];
  emptyOption?: boolean;
  hideClearButton?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  fetchUrl?: string;
  queryKey?: string | readonly unknown[];
  transformResponse?: (res: unknown) => unknown[];
  loading?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  getOptionLabel?: (option: unknown) => string;
  getOptionValue?: (option: unknown) => string;
  valueType?: 'string' | 'number';
  placeholder?: string;
};

type UseDeferredListFiltersParams = {
  storageKey?: string;
  filterDefinitions: FilterDefinition[];
  defaultFilters?: Record<string, unknown>;
};

type UseDeferredListFiltersReturn = {
  filtersConfig: FilterConfig[];
  appliedFilters: Record<string, unknown>;
  hasAppliedFilters: boolean;
  onApplyFilters: () => void;
  hasChanges: boolean;
};

/**
 * Deferred list filters with "Apply" button
 *
 * Provides declarative filter configuration with deferred application:
 * - Filters change immediately in UI (pending state)
 * - Data reloads only when "Apply" button is clicked (applied state)
 * - Visual indication of unsaved changes (hasChanges flag)
 * - Auto-generates onChange/onClear handlers for each filter
 * - Persists applied filters to localStorage
 *
 * @example
 * ```tsx
 * const { filtersConfig, appliedFilters, hasAppliedFilters, onApplyFilters, hasChanges } =
 *   useDeferredListFilters({
 *     storageKey: LOCAL_STORAGE_KEYS.SERVICES_LIST_FILTERS,
 *     filterDefinitions: [
 *       { type: 'select', name: 'service_type', label: t('Service Type'), options: service_types },
 *       { type: 'multiselect', name: 'locations', label: t('Locations'), options: locations },
 *       { type: 'boolean', name: 'active', label: t('Status') },
 *     ],
 *   })
 *
 * const { data } = useServicesListQuery(url, {
 *   ...params,
 *   ...(hasAppliedFilters ? { filters: appliedFilters } : {}),
 * })
 *
 * <ListFilters
 *   filters={filtersConfig}
 *   onApplyFilters={onApplyFilters}
 *   hasChanges={hasChanges}
 * />
 * ```
 */
export const useDeferredListFilters = (
  params: UseDeferredListFiltersParams,
): UseDeferredListFiltersReturn => {
  const { storageKey, filterDefinitions, defaultFilters = {} } = params;

  const [appliedFilters, setAppliedFilters] = useLocalStorage(
    storageKey,
    defaultFilters,
  );

  const [pendingFilters, setPendingFilters] = useState(() => appliedFilters);

  const removePendingFilter = useCallback((key: string) => {
    setPendingFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
  }, []);

  const onApplyFilters = useCallback(() => {
    setAppliedFilters(pendingFilters);
  }, [pendingFilters, setAppliedFilters]);

  const hasChanges = useMemo(
    () => JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters),
    [pendingFilters, appliedFilters],
  );

  const hasAppliedFilters = useMemo(
    () => Object.keys(appliedFilters).length > 0,
    [appliedFilters],
  );

  const filtersConfig: FilterConfig[] = useMemo(() => {
    return filterDefinitions.map((def) => {
      const { type, name, label, options, valueType = 'string', ...rest } = def;

      let onChange: FilterConfig['onChange'];
      if (type === 'multiselect') {
        onChange = (value) => {
          const arr = value as string[];
          const finalValue =
            valueType === 'number' ? arr.map(Number).filter(Boolean) : arr;
          setPendingFilters((prev) => ({
            ...prev,
            [name]: finalValue.length > 0 ? finalValue : undefined,
          }));
        };
      } else if (type === 'autocomplete') {
        onChange = (value) => {
          setPendingFilters((prev) => ({
            ...prev,
            [name]: value || undefined,
          }));
        };
      } else {
        onChange = (value) => {
          setPendingFilters((prev) => ({
            ...prev,
            [name]: value !== null && value !== '' ? value : undefined,
          }));
        };
      }

      const onClear = () => removePendingFilter(name);

      const toValue = (source: Record<string, unknown>) => {
        if (type === 'multiselect') {
          const raw = source[name];
          return Array.isArray(raw) ? raw.map(String) : [];
        }
        return source[name] ?? null;
      };

      return {
        type,
        name,
        label,
        value: toValue(pendingFilters),
        appliedValue: toValue(appliedFilters),
        onChange,
        onClear,
        options,
        ...rest,
      } as FilterConfig;
    });
  }, [filterDefinitions, pendingFilters, appliedFilters, removePendingFilter]);

  return {
    filtersConfig,
    appliedFilters,
    hasAppliedFilters,
    onApplyFilters,
    hasChanges,
  };
};
